import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Alert,
} from '@mui/material';
import { useApiSend } from '../../utils/httpClientTs';
import { setTempPassword as setTempPasswordApi } from '../../endpoints/login';
import { SetTempPasswordResponse } from '../../types/auth';
import AlertItem from '../../Components/AlertItem';
import {
  ContentCopy,
  Visibility,
  VisibilityOff,
  CheckCircle,
} from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import dayjs from 'dayjs';

interface SetTempPasswordDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  onSuccess?: () => void;
}

const SetTempPasswordDialog: React.FC<SetTempPasswordDialogProps> = ({
  open,
  onClose,
  userId,
  userName,
  onSuccess,
}) => {
  const [showPassword, setShowPassword] = useState<boolean>(true);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);

  const { mutate } = useApiSend<SetTempPasswordResponse, string>(
    setTempPasswordApi,
    (data) => {
      setTempPassword(data.temp_password);
      setExpiresAt(data.expires_at);
      setConfirmed(true);
      setShowPassword(true); // Explicitly show password when confirmed
      setIsSubmitting(false);
      // Don't call onSuccess here - wait until user closes the dialog after seeing the password
    },
    (error: any) => {
      const errorMessage =
        error?.message ||
        error?.error ||
        'Failed to set temporary password. Please try again.';
      setError(errorMessage);
      setIsSubmitting(false);
    }
  );

  const handleClose = () => {
    if (!confirmed) {
      // Only allow closing if not confirmed (password not shown yet)
      onClose();
      resetDialog();
    } else {
      // When closing after password is shown, call onSuccess to refresh data
      if (onSuccess) {
        onSuccess();
      }
      onClose();
      resetDialog();
    }
  };

  const resetDialog = () => {
    setShowPassword(true);
    setTempPassword(null);
    setExpiresAt(null);
    setError(null);
    setIsSubmitting(false);
    setConfirmed(false);
    setCopied(false);
  };

  const handleConfirm = () => {
    setError(null);
    setIsSubmitting(true);
    mutate(userId);
  };

  const handleCopyPassword = async () => {
    if (tempPassword) {
      try {
        await navigator.clipboard.writeText(tempPassword);
        setCopied(true);
        // Reset after 2 seconds
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy password:', err);
        setError('Failed to copy password to clipboard');
      }
    }
  };

  const formatExpirationDate = (dateString: string | null): string => {
    if (!dateString) return '';
    try {
      return dayjs(dateString).format('MMM DD, YYYY [at] h:mm A');
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={confirmed}
    >
      <DialogTitle>
        {confirmed ? 'Temporary Password Set' : 'Set Temporary Password'}
      </DialogTitle>
      <DialogContent>
        {!confirmed ? (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to set a temporary password for{' '}
              <strong>{userName}</strong>?
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Important:</strong> Setting a temporary password will:
              </Typography>
              <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                <li>Generate a secure temporary password</li>
                <li>Invalidate all existing user sessions</li>
                <li>Require the user to change their password on next login</li>
                <li>Expire after 3 days if not used</li>
              </ul>
            </Alert>
            {error && (
              <Box sx={{ mt: 2 }}>
                <AlertItem
                  severity="error"
                  description={error}
                  onClose={() => {}}
                  keepOpen={true}
                />
              </Box>
            )}
          </>
        ) : (
          tempPassword && (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  Temporary password has been set successfully!
                </Typography>
              </Alert>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  ⚠️ This password will only be shown once. Copy it now!
                </Typography>
              </Alert>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontWeight: 'medium' }}
                >
                  Temporary Password:
                </Typography>
                <TextField
                  fullWidth
                  value={tempPassword}
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  size="small"
                  autoFocus
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          title={
                            showPassword ? 'Hide password' : 'Show password'
                          }
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                        <IconButton
                          onClick={handleCopyPassword}
                          edge="end"
                          size="small"
                          color={copied ? 'success' : 'primary'}
                          title={copied ? 'Copied!' : 'Copy to clipboard'}
                        >
                          {copied ? <CheckCircle /> : <ContentCopy />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                    },
                  }}
                />
              </Box>
              <Button
                variant="outlined"
                startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                onClick={handleCopyPassword}
                fullWidth
                sx={{ mb: 2 }}
                color={copied ? 'success' : 'primary'}
              >
                {copied ? 'Copied to Clipboard!' : 'Copy Password'}
              </Button>
              {expiresAt && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Expires:</strong> {formatExpirationDate(expiresAt)}
                  </Typography>
                </Box>
              )}
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Please provide this password to the user securely. They will
                  be required to change it on their next login.
                </Typography>
              </Box>
            </>
          )
        )}
      </DialogContent>
      <DialogActions>
        {!confirmed ? (
          <>
            <Button onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <LoadingButton
              onClick={handleConfirm}
              variant="contained"
              color="primary"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Set Temporary Password
            </LoadingButton>
          </>
        ) : (
          <Button onClick={handleClose} variant="contained" color="primary">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SetTempPasswordDialog;
